/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import { tempToKelvin } from './bijvoetUtils';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import type { ScalesType } from '../Soundings/types';

export const isobarValues = [200, 300, 400, 500, 700, 850, 925, 1000];

interface DrawIsobarsProps {
  diagramType: SoundingDiagramType;
  minTemperature: number;
  maxTemperature: number;
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
}

export const drawIsobars = ({
  diagramType,
  minTemperature,
  maxTemperature,
  xScale,
  yScale,
  svg,
}: DrawIsobarsProps): void => {
  // For Bijvoet, the x-axis is NOT temperature but theta_es
  const xmin =
    diagramType === 'BIJVOET' ? tempToKelvin(minTemperature) : minTemperature;
  const xmax =
    diagramType === 'BIJVOET' ? tempToKelvin(maxTemperature) : maxTemperature;

  for (const pressureVal of isobarValues) {
    const Px = [];
    const tempBounds = [xmin, xmax];

    for (const bound of tempBounds) {
      Px.push([xScale(bound), yScale(pressureVal)]);
    }

    const lineGenerator = d3.line();
    const isoPathString = lineGenerator(Px as [number, number][]);
    const strID = `${pressureVal}-isobar`;
    svg
      .append('path')
      .attr('id', strID)
      .attr('type', 'isobar')
      .attr('d', isoPathString)
      .style('fill', 'none')
      .style('stroke-width', 1.5)
      .style('stroke', '#B5B51F')
      .style('stroke-opacity', 0.5);
  }
};
